import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Sparkles } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  TRANSFORMATION_CATEGORIES,
  type Category,
} from "@/lib/transformationCategories";

export const Route = createFileRoute("/_authenticated/create/template/gallery")({
  head: () => ({
    meta: [
      { title: "テンプレートから探す | KAGEM" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: TemplateGalleryPage,
});

function TemplateGalleryPage() {
  return (
    <main className="min-h-screen bg-background px-4 py-8 sm:py-10">
      <div className="mx-auto max-w-6xl">
        <Link
          to="/create/template"
          className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          自由記述に戻る
        </Link>

        <header className="mb-8 text-center">
          <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-3 py-1 text-xs font-semibold text-primary">
            <Sparkles className="h-3.5 w-3.5" />
            変身テンプレ
          </div>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
            テンプレートから探す
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            写真をアップロードして、好きな変身カテゴリを選ぼう
          </p>
        </header>

        <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4">
          {TRANSFORMATION_CATEGORIES.map((c) => (
            <CategoryCard key={c.id} category={c} />
          ))}
        </div>
      </div>
    </main>
  );
}

function CategoryCard({ category }: { category: Category }) {
  const gradient = category.cardGradient ?? "from-primary/40 to-primary/10";
  return (
    <Link
      to="/create/template/category/$category"
      params={{ category: category.id }}
      aria-label={`${category.label}に変身ページへ`}
      className="group relative flex aspect-[3/4] flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      <div className="relative flex-1 overflow-hidden">
        {category.cardImageUrl ? (
          <img
            src={category.cardImageUrl}
            alt=""
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div
            className={cn(
              "flex h-full w-full items-center justify-center bg-gradient-to-br",
              gradient,
            )}
          >
            <span className="text-6xl drop-shadow-md transition-transform duration-300 group-hover:scale-110">
              {category.emoji}
            </span>
          </div>
        )}
      </div>
      <div className="relative bg-black/60 px-3 py-2.5 text-white backdrop-blur-sm">
        <p className="truncate text-sm font-bold">{category.label}</p>
        {category.description && (
          <p className="truncate text-xs text-white/70">{category.description}</p>
        )}
      </div>
    </Link>
  );
}
